/* eslint-disable no-unused-vars */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import AppHeader from '../AppHeader';
import { useLazyQuery } from '@apollo/client';
import SummaryCard, {
  SummaryDataItem,
} from '../SummaryCard';
import ErrorMessage from '../ErrorMessage';
import {
  DATA_FETCH_ERROR,
  NO_CONNECTION_ERROR,
  ONLOAN_INVALID_STOREID_ERROR,
  LABOR,
  SALES,
  BALLOONS,
  INVENTORY_CONTROL,
  OMNI_CHANNEL,
} from '../../common/constants';
import './styles.scss';
import { useNavigate } from 'react-router-dom';
import SelectDropdown from '../SelectDropdown';
import {
  GET_DEFAULT_LOCATION,
  GET_LABOR_SUMMARY,
  GET_SALES_SUMMARY,
  GET_INVENTORY,
  GET_INVENTORY_EXPANDABLE_VIEW,
  GET_SERVICES_SUMMARY_DRIVERS_DETAILS_SUMMARY,
  GET_SERVICES_EXPANDED_TOP_LEVEL_DATA,
  GET_OMNI_CHANNEL_SUMMARY,
  GET_OMNI_CHANNEL_EXPANDED_TOP_LEVEL_DATA,
} from '../../graphql/queries.js';
import {
  getDropdownOptions,
  getDate,
  toTitleCase,
  toFixedVal,
  getStoreChecker,
  nullCheck,
  getLocationNameWithoutState,
  getLocationStateToken,
} from '../../utils/utils';
import Modal, { InventoryModal } from '../Modal';
import useNetwork from '../UseNetwork';
import {
  SalesSummaryModalServiceContent,
  SalesSummaryModalOmniContent,
  SalesSummaryModalInventoryContent,
} from './SalesSummaryModalGrids';

const filterContainerStyles = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  mt: 1,
};

const customStyles = {
  option: (provided, state) => ({
    ...provided,
    fontSize: '1.6rem',
    color: state.isSelected ? '#FFFFFF' : '#353535',
    padding: 5,
  }),
};

const SalesSummary = ({ employeeInfo = {} }) => {
  let navigate = useNavigate();
  const [options, setOptions] = React.useState([]);
  const networkStatus = useNetwork();
  const [selectedDateMatrix, setSelectedDateMatrix] = React.useState(null);
  const [selectedDate, setSelectedDate] = React.useState(null);
  const userEmail = employeeInfo.adUpn;
  const [selectedLocation, setSelectedLocation] = React.useState(
    JSON.parse(sessionStorage.getItem('selectedLocation'))
  );
  let selectedStoreId = localStorage.getItem('storeId');
  const [laborDateMatrix, setLaborDateMatrix] = React.useState(null);
  const activeStorechecker = getStoreChecker(selectedStoreId);

  const [
    getSalesSummary,
    {
      data: { salesSummaryV2: { salesReport = [] } = {} } = {},
      error = {},
      loading,
    },
  ] = useLazyQuery(GET_SALES_SUMMARY);

  const [
    getDefaultLocation,
    { error: locationError = {}, loading: locationLoading },
  ] = useLazyQuery(GET_DEFAULT_LOCATION, {
    onCompleted: (data) => {
      let { salesSummaryV2 } = data;
      if (selectedLocation.locationName === 'NA') {
        setSelectedLocation({
          locationNo: salesSummaryV2?.location?.primaryLocationNumber,
          locationName: salesSummaryV2?.location?.primaryLocationName,
        });
        sessionStorage.setItem(
          'selectedLocation',
          JSON.stringify({
            locationNo: salesSummaryV2?.location?.primaryLocationNumber,
            locationName: salesSummaryV2?.location?.primaryLocationName,
          })
        );
      }
    },
  });

  const [
    getServicesSummary,
    {
      data: { services: { services = [] } = {} } = {},
      error: services_error = {},
      loading: services_loading,
    },
  ] = useLazyQuery(GET_SERVICES_SUMMARY_DRIVERS_DETAILS_SUMMARY);

  const [
    getLaborSummary,
    {
      data: { labor: { labor = [] } = {} } = {},
      error: laborError = {},
      loading: laborLoading,
    },
  ] = useLazyQuery(GET_LABOR_SUMMARY);

  const [
    getInventory,
    {
      data: { inventoryControl: { inventoryControl = [] } = {} } = {},
      error: inventoryerror = {},
      loading: inventoryloading,
    },
  ] = useLazyQuery(GET_INVENTORY);

  useEffect(() => {
    let options = [];
    options = getDropdownOptions();
    setOptions(options);
    const selectedDateValue = localStorage.hasOwnProperty('selectedDate')
      ? localStorage.getItem('selectedDate')
      : 'YESTERDAY';
    const defaultValue = options.find((obj) => obj.value === selectedDateValue);
    if (defaultValue) {
      setSelectedDate(defaultValue);
      setSelectedDateMatrix(defaultValue.value);
    }
  }, [setOptions, setSelectedDate]);

  useEffect(() => {
    if (userEmail && (activeStorechecker === true || selectedStoreId === '')) {
      getDefaultLocation({
        variables: { request: { email: userEmail, storeId: selectedStoreId } },
        context: {
          headers: {
            Authorization: localStorage.getItem('jwtToken'),
          },
        },
      });
    }
  }, [userEmail, getDefaultLocation, selectedStoreId, activeStorechecker]);

  useEffect(() => {
    if (
      userEmail &&
      selectedDateMatrix &&
      selectedLocation &&
      selectedLocation?.locationName !== 'NA'
    ) {
      getServicesSummary({
        variables: {
          request: {
            email: userEmail,
            location: {
              primaryLocationNumber: selectedLocation?.locationNo,
              primaryLocationName: selectedLocation?.locationName,
            },
            toDate: getDate('YESTERDAY', ''),
            matrixType:
              selectedDateMatrix === 'YESTERDAY' ? 'DAY' : selectedDateMatrix,
          },
        },
        context: {
          headers: {
            Authorization: localStorage.getItem('jwtToken'),
          },
        },
      });
    }
  }, [userEmail, selectedDateMatrix, selectedLocation, getServicesSummary]);
  useEffect(() => {
    if (
      userEmail &&
      selectedDateMatrix &&
      selectedLocation &&
      selectedLocation?.locationName !== 'NA'
    ) {
      getSalesSummary({
        variables: {
          request: {
            email: userEmail,
            location: {
              primaryLocationNumber: selectedLocation?.locationNo,
              primaryLocationName: selectedLocation?.locationName,
            },
            toDate: getDate('YESTERDAY', ''),
            matrixType: selectedDateMatrix,
          },
        },
        context: {
          headers: {
            Authorization: localStorage.getItem('jwtToken'),
          },
        },
      });
    }
  }, [userEmail, selectedDateMatrix, selectedLocation, getSalesSummary]);

  useEffect(() => {
    if (
      userEmail &&
      selectedDateMatrix &&
      selectedLocation &&
      selectedLocation.locationName !== 'NA'
    ) {
      const laborMatrix =
        selectedDateMatrix === 'YESTERDAY' || selectedDateMatrix === 'WTD'
          ? 'LCW'
          : selectedDateMatrix;
      setLaborDateMatrix(laborMatrix);
      getLaborSummary({
        variables: {
          request: {
            email: userEmail,
            toDate: getDate('YESTERDAY', ''),
            location: {
              primaryLocationNumber: selectedLocation?.locationNo,
              primaryLocationName: selectedLocation?.locationName,
            },
            matrixType: laborMatrix,
          },
        },
        context: {
          headers: {
            Authorization: localStorage.getItem('jwtToken'),
          },
        },
      });
    }
  }, [userEmail, selectedLocation, selectedDateMatrix, getLaborSummary]);

  useEffect(() => {
    if (
      userEmail &&
      selectedDateMatrix &&
      selectedLocation &&
      selectedLocation?.locationName !== 'NA'
    ) {
      getInventory({
        variables: {
          request: {
            email: userEmail,
            location: {
              primaryLocationNumber: selectedLocation?.locationNo,
              primaryLocationName: selectedLocation?.locationName,
            },
            toDate: getDate('YESTERDAY', ''),
            matrixType: selectedDateMatrix,
          },
        },
        context: {
          headers: {
            Authorization: localStorage.getItem('jwtToken'),
          },
        },
      });
    }
  }, [userEmail, selectedDateMatrix, selectedLocation, getInventory]);

  const {
    balloon = {
      totalSalesUnits: 0,
      totalSalesAmount: 0,
      serviceUnits: 0,
    },
  } = services || {};

  const {
    laborDetails = {
      primaryActualPayrollHrs: 0,
      primaryPayrollPlan: 0,
      primaryOvertimePercentage: 0,
      primaryEfficiency: 0,
      secondaryActualPayrollHrs: 0,
      secondaryPayrollPlan: 0,
      secondaryEfficiency: 0,
      secondaryOvertimePercentage: 0,
    },
  } = labor || {};

  const {
    extNetDsctAmt = 0,
    performanceToPlan = 0,
    compPercent = 0,
    emailCapturePct = 0,
  } = salesReport[0] || {};

  const handleSalesCardClick = (e) => {
    navigate('/sales-details/drivers');
  };

  const handleLaborCardClick = (e) => {
    navigate('/labor-details/labor');
  };

  const handleServicesCardClick = (e) => {
    navigate('/services-details/drivers');
  };

  const handleOmniChannelCardClick = (e) => {
    navigate('/omnichannel-details/bopis');
  };

  const handleInventoryControlCardClick = (e) => {
    navigate('/inventory-details/drivers');
  };

  const handleDateChange = (e) => {
    setSelectedDateMatrix(e.value);
    setSelectedDate(e);
    localStorage.setItem('selectedDate', e.value);
  };

  const [
    getSalesSummaryMatrixData,
    {
      data: dataSalesMatrix = {},
      // eslint-disable-next-line no-unused-vars
      error: salesError = {},
    },
  ] = useLazyQuery(GET_SALES_SUMMARY);

  useEffect(() => {
    if (
      userEmail &&
      selectedDateMatrix &&
      selectedLocation &&
      selectedLocation?.locationName !== 'NA'
    ) {
      getSalesSummaryMatrixData({
        variables: {
          request: {
            email: userEmail,
            location: {
              primaryLocationNumber: selectedLocation?.locationNo,
              primaryLocationName: selectedLocation?.locationName,
            },
            toDate: getDate('YESTERDAY', ''),
            matrixType: selectedDateMatrix,
          },
        },
        context: {
          headers: {
            Authorization: localStorage.getItem('jwtToken'),
          },
        },
      });
    }
  }, [
    userEmail,
    selectedDateMatrix,
    selectedLocation,
    getSalesSummaryMatrixData,
  ]);

  const salesMatrixData = dataSalesMatrix?.salesSummaryV2?.salesMatrix;

  const [
    getInventoryExpandedData,
    {
      data: {
        inventoryControl: {
          inventoryControlExpandableView: {
            title = '',
            chain,
            regionList = [],
          } = {},
        } = {},
      } = {},
      expandedInventoryerror,
      expandedInventoryloading,
    },
  ] = useLazyQuery(GET_INVENTORY_EXPANDABLE_VIEW);

  useEffect(() => {
    if (
      userEmail &&
      selectedDateMatrix &&
      selectedLocation &&
      selectedLocation?.locationName !== 'NA'
    ) {
      getInventoryExpandedData({
        variables: {
          request: {
            email: userEmail,
            location: {
              primaryLocationNumber: selectedLocation?.locationNo,
              primaryLocationName: selectedLocation?.locationName,
            },
            toDate: getDate('YESTERDAY', ''),
            matrixType: selectedDateMatrix,
          },
        },
        context: {
          headers: {
            Authorization: localStorage.getItem('jwtToken'),
          },
        },
      });
    }
  }, [
    userEmail,
    selectedDateMatrix,
    selectedLocation,
    getInventoryExpandedData,
  ]);

  const [
    getExpandedServicesTopLevelData,
    {
      data: {
        services: {
          servicesExpandableView: {
            title: serviceExpandedTitle = '',
            chain: serviceExpandedChain,
            regionList: serviceExpandedRegionList = [],
          } = {},
        } = {},
      } = {},
      error: expandedServiceTopLevelError,
      loading: expandedServiceTopLevelLoading,
    },
  ] = useLazyQuery(GET_SERVICES_EXPANDED_TOP_LEVEL_DATA);

  useEffect(() => {
    if (
      userEmail &&
      selectedDateMatrix &&
      selectedLocation &&
      selectedLocation?.locationName !== 'NA'
    ) {
      getExpandedServicesTopLevelData({
        variables: {
          request: {
            email: userEmail,
            location: {
              primaryLocationNumber: selectedLocation?.locationNo,
              primaryLocationName: selectedLocation?.locationName,
            },
            toDate: getDate('YESTERDAY', ''),
            matrixType:
              selectedDateMatrix === `YESTERDAY` ? `DAY` : selectedDateMatrix,
          },
        },
        context: {
          headers: {
            Authorization: localStorage.getItem('jwtToken'),
          },
        },
      });
    }
  }, [
    userEmail,
    selectedDateMatrix,
    selectedLocation,
    getExpandedServicesTopLevelData,
  ]);

  const [
    getOmniChannelSummaryData,
    {
      data: { omni: { omni: { bopis } = {} } = {} } = {},
      error: omniChannelLandingError,
      loading: omniChannelLandingLoading,
    },
  ] = useLazyQuery(GET_OMNI_CHANNEL_SUMMARY);

  useEffect(() => {
    if (
      userEmail &&
      selectedDateMatrix &&
      selectedLocation &&
      selectedLocation?.locationName !== 'NA'
    ) {
      getOmniChannelSummaryData({
        variables: {
          request: {
            email: userEmail,
            location: {
              primaryLocationNumber: selectedLocation?.locationNo,
              primaryLocationName: selectedLocation?.locationName,
            },
            toDate: getDate('YESTERDAY', ''),
            matrixType:
              selectedDateMatrix === `YESTERDAY` ? `DAY` : selectedDateMatrix,
          },
        },
        context: {
          headers: {
            Authorization: localStorage.getItem('jwtToken'),
          },
        },
      });
    }
  }, [
    userEmail,
    selectedDateMatrix,
    selectedLocation,
    getOmniChannelSummaryData,
  ]);

  const [
    getExpandedOmniChannelTopLevelData,
    {
      data: {
        omni: {
          omniExpandableView: {
            title: omniChannelExpandedTitle = '',
            chain: omniChannelExpandedChain,
            regionList: omniChannelExpandedRegionList = [],
          } = {},
        } = {},
      } = {},
      error: expandedOmniChannelTopLevelError,
      loading: expandedOmniChannelTopLevelLoading,
    },
  ] = useLazyQuery(GET_OMNI_CHANNEL_EXPANDED_TOP_LEVEL_DATA);

  useEffect(() => {
    if (
      userEmail &&
      selectedDateMatrix &&
      selectedLocation &&
      selectedLocation?.locationName !== 'NA'
    ) {
      getExpandedOmniChannelTopLevelData({
        variables: {
          request: {
            email: userEmail,
            location: {
              primaryLocationNumber: selectedLocation?.locationNo,
              primaryLocationName: selectedLocation?.locationName,
            },
            toDate: getDate('YESTERDAY', ''),
            matrixType:
              selectedDateMatrix === `YESTERDAY` ? `DAY` : selectedDateMatrix,
          },
        },
        context: {
          headers: {
            Authorization: localStorage.getItem('jwtToken'),
          },
        },
      });
    }
  }, [
    userEmail,
    selectedDateMatrix,
    selectedLocation,
    getExpandedOmniChannelTopLevelData,
  ]);

  const modalLaborSummaryContent = (
    <>
      <SummaryDataItem
        itemAmount={Math.round(
          laborDetails?.secondaryActualPayrollHrs
        ).toLocaleString('en')}
        itemTitle="Payroll Hrs"
      />
      <SummaryDataItem
        itemAmount={Math.round(
          laborDetails?.secondaryPayrollPlan
        ).toLocaleString('en')}
        itemTitle="Payroll Plan"
      />
      <SummaryDataItem
        itemAmount={toFixedVal(
          laborDetails?.secondaryEfficiency,
          '%',
          'end',
          2
        )}
        itemTitle="Efficiency"
      />
      <SummaryDataItem
        itemAmount={toFixedVal(
          laborDetails?.secondaryOvertimePercentage,
          '%',
          'end',
          2
        )}
        itemTitle="OT%"
      />
    </>
  );

  const modalSalesSummaryContent = (
    <>
      <SummaryDataItem
        itemAmount={Math.round(
          nullCheck(salesMatrixData?.sales?.secondary)
        ).toLocaleString('en')}
        itemTitle="Sales $"
      />
      <SummaryDataItem
        itemAmount={toFixedVal(
          salesMatrixData?.sales?.secondaryVsPlan,
          '%',
          'end',
          1
        )}
        itemTitle="Plan %"
      />
      <SummaryDataItem
        itemAmount={toFixedVal(
          salesMatrixData?.sales?.secondaryCompPercentage,
          '%',
          'end',
          1
        )}
        itemTitle="Comp %"
      />
      <SummaryDataItem
        itemAmount={toFixedVal(
          salesMatrixData?.email?.secondary,
          '%',
          'end',
          1
        )}
        itemTitle="Email %"
      />
    </>
  );

  const modalServiceSummaryContent = (
    <SalesSummaryModalServiceContent
      expandedServiceTopLevelLoading={expandedServiceTopLevelLoading}
      expandedServiceTopLevelError={expandedServiceTopLevelError}
      serviceExpandedTitle={serviceExpandedTitle}
      serviceExpandedChain={serviceExpandedChain}
      serviceExpandedRegionList={serviceExpandedRegionList}
    />
  );

  const modalOmniChannelSummaryContent = (
    <SalesSummaryModalOmniContent
      expandedOmniChannelTopLevelLoading={expandedOmniChannelTopLevelLoading}
      expandedOmniChannelTopLevelError={expandedOmniChannelTopLevelError}
      omniChannelExpandedTitle={omniChannelExpandedTitle}
      omniChannelExpandedChain={omniChannelExpandedChain}
      omniChannelExpandedRegionList={omniChannelExpandedRegionList}
    />
  );

  const modalInventorySummaryContent = (
    <SalesSummaryModalInventoryContent
      expandedInventoryloading={expandedInventoryloading}
      expandedInventoryerror={expandedInventoryerror}
      title={title}
      chain={chain}
      regionList={regionList}
    />
  );

  useEffect(() => {
    if (globalThis.newrelic) {
      setTimeout(function Pagecallerfun() {
        globalThis.newrelic.addPageAction('Sales-Summary', { result: 'success' });
      }, 1000);
    }
  }, []);

  const primarylocation = selectedLocation?.locationName;
  const statelocation = getLocationStateToken(primarylocation);
  const withoutstatelocation = getLocationNameWithoutState(primarylocation);

  return (
    <AppHeader
        headerContent={
          <Box sx={filterContainerStyles}>
            {locationLoading ? (
              <CircularProgress color="inherit" size={30} />
            ) : (
              <div className="locationRow">
                {toTitleCase(withoutstatelocation)}
                {primarylocation === 'NA' || statelocation.length !== 2 ? (
                  ''
                ) : (
                  <span>, {statelocation}</span>
                )}
              </div>
            )}
            {options.length > 0 ? (
              <div className="blue-dropdown">
                <SelectDropdown
                  className="dateRow"
                  styles={customStyles}
                  onChange={handleDateChange}
                  options={options}
                  value={selectedDate}
                  isSearchable={false}
                />
              </div>
            ) : (
              <CircularProgress color="inherit" size={30} />
            )}
          </Box>
        }
        backNaviagtorHidden={true}
      >
        {activeStorechecker === false && selectedStoreId ? (
          <ErrorMessage message={ONLOAN_INVALID_STOREID_ERROR} />
        ) : null}
        {error.message ||
        locationError.message ||
        services_error.message ||
        laborError.message ||
        inventoryerror.message ||
        omniChannelLandingError?.message ? (
          <ErrorMessage
            statusCode={
              error?.networkError?.statusCode ||
              locationError?.networkError?.statusCode ||
              services_error?.networkError?.statusCode ||
              laborError?.networkError?.statusCode ||
              inventoryerror?.networkError?.statusCode ||
              omniChannelLandingError?.networkError?.statusCode
            }
            message={
              error?.networkError?.statusCode ||
              locationError?.networkError?.statusCode ||
              services_error?.networkError?.statusCode ||
              laborError?.networkError?.statusCode ||
              inventoryerror?.networkError?.statusCode ||
              omniChannelLandingError?.networkError?.statusCode
                ? DATA_FETCH_ERROR
                : laborError.message
            }
          />
        ) : null}

        {!networkStatus && <ErrorMessage message={NO_CONNECTION_ERROR} />}
        <div className="sales-summary">
          <div className="SummaryCard-container">
            <Modal
              SalesSummaryCard="true"
              title={' Sales Summary'}
              titleInModal={salesMatrixData?.secondTitle}
              ExpandedSalesSummaryComponent="true"
              primaryLocationNumber={selectedLocation?.locationNo}
              primaryLocationName={selectedLocation?.locationName}
              selectedDateMatrix={selectedDateMatrix}
              modalContentOther={modalSalesSummaryContent}
            />
            <div>
              <SummaryCard
                title={SALES}
                handleSalesCardClick={handleSalesCardClick}
                primaryLocationName={selectedLocation?.locationName}
                selectedDateMatrix={selectedDateMatrix}
              >
                {loading && (
                  <div className="data-row">
                    <CircularProgress color="primary" />
                  </div>
                )}
                {!loading && (
                  <>
                    <div className="data-row">
                      <SummaryDataItem
                        itemAmount={toFixedVal(
                          extNetDsctAmt,
                          '$',
                          'start',
                          0
                        )}
                        itemTitle="Sales $"
                      />
                    
                      <SummaryDataItem
                         itemAmount={toFixedVal(
                          performanceToPlan,
                          '%',
                          'end',
                          1
                         )}
                        itemTitle="Plan %"
                      />
                    </div>
                    <div className="data-row">
                      
                      <SummaryDataItem
                        itemAmount={toFixedVal(compPercent, '%', 'end', 1)}
                        itemTitle="Comp %"
                      />
                      <SummaryDataItem
                        itemAmount={toFixedVal(emailCapturePct, '%', 'end', 1)}
                        itemTitle="Email %"
                      />
                    </div>
                  </>
                )}
              </SummaryCard>
            </div>
          </div>
          <div className="ServicesCard-container">
            <Modal
              title={`${BALLOONS} Summary`}
              titleInModal={
                serviceExpandedTitle ? salesMatrixData?.secondTitle : null
              }
              ExpandedServiceSummaryComponent="true"
              primaryLocationNumber={selectedLocation?.locationNo}
              primaryLocationName={selectedLocation?.locationName}
              selectedDateMatrix={selectedDateMatrix}
              modalContentOther={modalServiceSummaryContent}
            />
            <div>
              <SummaryCard
                title={BALLOONS}
                handleServicesCardClick={handleServicesCardClick}
                primaryLocationName={selectedLocation?.locationName}
                selectedDateMatrix={selectedDateMatrix}
              >
                {services_loading && (
                  <div className="data-row">
                    <CircularProgress color="primary" />
                  </div>
                )}
                {!services_loading && (
                    <div style={{ paddingTop: '3px' }} className="data-row">
                      <SummaryDataItem
                        itemAmount={
                          `$ ` +
                          Math.round(balloon?.totalSalesAmount).toLocaleString(
                            'en'
                          )
                        }
                        itemTitle="Balloon Sales $"
                      />
                      <SummaryDataItem
                        itemAmount={Math.round(
                          balloon?.totalSalesUnits
                        ).toLocaleString('en')}
                        itemTitle="Balloon Sales U"
                      />
                    </div>
                )}
              </SummaryCard>
            </div>
          </div>
          <div className="OmniChannelCard-container">
            <Modal
              title={`${OMNI_CHANNEL} Summary`}
              titleInModal={
                omniChannelExpandedTitle ? salesMatrixData?.secondTitle : null
              }
              ExpandedOmniChannelComponent="true"
              primaryLocationNumber={selectedLocation?.locationNo}
              primaryLocationName={selectedLocation?.locationName}
              selectedDateMatrix={selectedDateMatrix}
              modalContentOther={modalOmniChannelSummaryContent}
            />
            <div>
              <SummaryCard
                title={OMNI_CHANNEL}
                handleOmniChannelCardClick={handleOmniChannelCardClick}
                primaryLocationName={selectedLocation?.locationName}
                selectedDateMatrix={selectedDateMatrix}
              >
                {omniChannelLandingLoading && (
                  <div className="data-row">
                    <CircularProgress color="primary" />
                  </div>
                )}
                {!omniChannelLandingLoading && (
                  <>
                    <div style={{ paddingTop: '3px' }} className="data-row">
                      <SummaryDataItem
                        itemAmount={Math.round(
                          bopis?.fulfilledOrdersCnt ?? 0
                        ).toLocaleString('en')}
                        itemTitle="BOPIS Orders Fulfilled"
                      />
                      <SummaryDataItem
                        itemAmount={
                          Math.round(bopis?.pickRate ?? 0).toLocaleString(
                            'en'
                          ) + `%`
                        }
                        itemTitle="BOPIS Pick Rate"
                      />
                    </div>
                  </>
                )}
              </SummaryCard>
            </div>
          </div>
          <div className="LaborCard-container">
            <Modal
              title={' Labor Summary'}
              titleInModal={labor?.secondTitle}
              ExpandedLaborSummaryComponent="true"
              primaryLocationNumber={selectedLocation?.locationNo}
              primaryLocationName={selectedLocation?.locationName}
              selectedDateMatrix={laborDateMatrix}
              modalContentOther={modalLaborSummaryContent}
            />
            <div>
              <SummaryCard
                title={LABOR}
                handleLaborCardClick={handleLaborCardClick}
              >
                {laborLoading && (
                  <div className="data-row">
                    <CircularProgress color="primary" />
                  </div>
                )}
                {!laborLoading && (
                  <>
                    <div className="data-row">
                      <SummaryDataItem
                        itemAmount={Math.round(
                          laborDetails?.primaryActualPayrollHrs
                        ).toLocaleString('en')}
                        itemTitle="Actual Payroll Hrs"
                      />
                      <SummaryDataItem
                        itemAmount={Math.round(
                          laborDetails?.primaryPayrollPlan
                        ).toLocaleString('en')}
                        itemTitle="Payroll Plan"
                      />
                    </div>
                    <div className="data-row">
                      <SummaryDataItem
                        itemAmount={toFixedVal(
                          laborDetails?.primaryEfficiency,
                          '%',
                          'end',
                          2
                        )}
                        itemTitle="Efficiency %"
                      />
                      <SummaryDataItem
                        itemAmount={toFixedVal(
                          laborDetails?.primaryOvertimePercentage,
                          '%',
                          'end',
                          2
                        )}
                        itemTitle="OT%"
                      />
                    </div>
                  </>
                )}
              </SummaryCard>
            </div>
          </div>
          {
            <div className="Inventory-container">
              <InventoryModal
                title={INVENTORY_CONTROL}
                titleInModal={salesMatrixData?.secondTitle}
                ExpandedInventorySummaryComponent="true"
                primaryLocationNumber={selectedLocation?.locationNo}
                primaryLocationName={selectedLocation?.locationName}
                selectedDateMatrix={selectedDateMatrix}
                modalContentOther={modalInventorySummaryContent}
              />
              <div>
                <SummaryCard
                  title="Inventory Control"
                  handleInventoryControlCardClick={
                    handleInventoryControlCardClick
                  }
                  primaryLocationName={selectedLocation?.locationName}
                  selectedDateMatrix={selectedDateMatrix}
                >
                  {inventoryloading && (
                    <div className="data-row">
                      <CircularProgress color="primary" />
                    </div>
                  )}
                  {!inventoryloading && (
                    <>
                      <div className="data-row">
                        <SummaryDataItem
                          itemAmount={Math.round(
                            inventoryControl?.replace?.unitQuantity ?? 0
                          ).toLocaleString('en')}
                          itemTitle="Replace"
                        />
                        <SummaryDataItem
                          itemAmount={Math.round(
                            inventoryControl?.damages?.unitQuantity ?? 0
                          ).toLocaleString('en')}
                          itemTitle="Damages"
                        />
                      </div>
                      <div className="data-row">
                        <SummaryDataItem
                          itemAmount={Math.round(
                            inventoryControl?.markedOutOfStock?.unitQuantity ??
                              0
                          ).toLocaleString('en')}
                          itemTitle="MOS"
                        />
                        <SummaryDataItem
                          itemAmount={Math.round(
                            inventoryControl?.knownTheft?.unitQuantity ?? 0
                          ).toLocaleString('en')}
                          itemTitle="Known Theft"
                        />
                      </div>
                      <div className="data-row">
                        <SummaryDataItem
                          itemAmount={Math.round(
                            inventoryControl?.shipment?.unitQuantity ?? 0
                          ).toLocaleString('en')}
                          itemTitle="Shipment"
                        />
                        <SummaryDataItem
                          itemAmount={Math.round(
                            inventoryControl?.candy?.unitQuantity ?? 0
                          ).toLocaleString('en')}
                          itemTitle="Candy"
                        />
                      </div>
                    </>
                  )}
                </SummaryCard>
              </div>
            </div>
          }
        </div>
      </AppHeader>
  );
};

SalesSummary.propTypes = {
  employeeInfo: PropTypes.shape({
    adUpn: PropTypes.string,
  }),
};

const mapStateToProps = (state) => ({
  employeeInfo: state.auth.employeeInfo,
});

export default connect(mapStateToProps, undefined)(SalesSummary);
